import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useBrand } from "../lib/brand";
import { PageHeader, Empty, Chip, Modal } from "../components/ui-bits";
import { UserPlus, Trash2, Mail, Users as UsersIcon, RotateCcw } from "lucide-react";

const BRAND_ROLES = [
  "brand_owner", "brand_admin", "designer", "product_manager",
  "production_manager", "pattern_maker", "supplier", "client_viewer",
];

const roleLabel = (r) => r?.replaceAll("_", " ") || "—";

export default function TeamPage() {
  const { activeBrand } = useBrand();
  const [tab, setTab] = useState("members");
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [showInvite, setShowInvite] = useState(false);
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");

  const load = () => {
    if (!activeBrand) return;
    api.get(`/brands/${activeBrand.id}/members`).then(r=>setMembers(r.data)).catch(()=>{});
    api.get(`/brands/${activeBrand.id}/invitations`).then(r=>setInvites(r.data)).catch(()=>setInvites([]));
  };
  useEffect(() => { load(); }, [activeBrand]);

  const changeRole = async (m, role) => {
    try {
      await api.patch(`/brands/${activeBrand.id}/members/${m.id}`, { brand_role: role });
      setOk("Role updated"); load(); setTimeout(()=>setOk(""), 2000);
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const remove = async (m) => {
    if (!window.confirm(`Remove ${m.user?.email} from ${activeBrand.name}?`)) return;
    try {
      await api.delete(`/brands/${activeBrand.id}/members/${m.id}`);
      load();
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const cancelInvite = async (inv) => {
    if (!window.confirm(`Cancel invite to ${inv.email}?`)) return;
    await api.delete(`/brands/${activeBrand.id}/invitations/${inv.id}`);
    load();
  };

  const pending = invites.filter(i=>i.status==="pending");

  return (
    <div data-testid="team-page">
      <PageHeader
        eyebrow={activeBrand?.name}
        title="Team Members"
        description="Invite colleagues, suppliers or clients to collaborate inside this brand workspace."
        action={<button data-testid="invite-btn" onClick={()=>setShowInvite(true)} className="btn-gold inline-flex items-center gap-2"><UserPlus size={14}/> Invite member</button>}
      />

      <div className="flex gap-1 border-b border-[#E5E5E5] mb-6">
        <button data-testid="tab-members" onClick={()=>setTab("members")} className={`px-5 py-3 text-xs uppercase tracking-widest border-b-2 ${tab==="members"?"border-[#0A0A0A] text-[#0A0A0A]":"border-transparent text-[#525252] hover:text-[#0A0A0A]"}`}>Members ({members.length})</button>
        <button data-testid="tab-invites" onClick={()=>setTab("invites")} className={`px-5 py-3 text-xs uppercase tracking-widest border-b-2 ${tab==="invites"?"border-[#0A0A0A] text-[#0A0A0A]":"border-transparent text-[#525252] hover:text-[#0A0A0A]"}`}>Pending invitations ({pending.length})</button>
      </div>

      {ok && <div className="text-green-400 text-sm mb-3">{ok}</div>}
      {error && <div className="text-red-600 text-sm mb-3">{error}</div>}

      {tab === "members" && (
        members.length === 0 ? <Empty title="No members yet"/> : (
          <div className="card-dark">
            <table className="tbl" data-testid="members-table">
              <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Status</th><th></th></tr></thead>
              <tbody>
                {members.map(m => (
                  <tr key={m.id} data-testid={`member-row-${m.id}`}>
                    <td>{m.user?.name || "—"}</td>
                    <td className="text-[#525252]">{m.user?.email || "—"}</td>
                    <td>
                      <select data-testid={`member-role-${m.id}`} disabled={m.brand_role === "brand_owner"} className="input-dark py-1 text-xs" value={m.brand_role} onChange={(e)=>changeRole(m, e.target.value)}>
                        {BRAND_ROLES.map(r => <option key={r} value={r}>{roleLabel(r)}</option>)}
                      </select>
                    </td>
                    <td><Chip tone="green">active</Chip></td>
                    <td className="text-right">
                      {m.brand_role !== "brand_owner" && (
                        <button data-testid={`member-remove-${m.id}`} onClick={()=>remove(m)} className="text-[#525252] hover:text-red-600"><Trash2 size={14}/></button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {tab === "invites" && (
        pending.length === 0 ? <Empty title="No pending invitations"/> : (
          <div className="card-dark">
            <table className="tbl" data-testid="invites-table">
              <thead><tr><th>Email</th><th>Role</th><th>Status</th><th>Sent</th><th></th></tr></thead>
              <tbody>
                {pending.map(i => (
                  <tr key={i.id}>
                    <td className="text-[#0A0A0A]"><Mail size={12} className="inline mr-2"/>{i.email}</td>
                    <td>{roleLabel(i.brand_role)}</td>
                    <td><Chip tone="gold">{i.status}</Chip></td>
                    <td className="text-[#525252] text-xs">{new Date(i.created_at).toLocaleDateString()}</td>
                    <td className="text-right">
                      <button onClick={()=>cancelInvite(i)} className="text-[#525252] hover:text-red-600" data-testid={`invite-cancel-${i.id}`}><Trash2 size={14}/></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {showInvite && <InviteModal brandId={activeBrand?.id} onClose={()=>setShowInvite(false)} onDone={()=>{setShowInvite(false); load();}}/>}
    </div>
  );
}

function InviteModal({ brandId, onClose, onDone }) {
  const [form, setForm] = useState({ email: "", brand_role: "designer", message: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setError("");
    try {
      await api.post(`/brands/${brandId}/invitations`, form);
      onDone();
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); }
    setSaving(false);
  };
  return (
    <Modal open={true} onClose={onClose} title="Invite member">
      <form onSubmit={submit} className="space-y-4" data-testid="invite-modal">
        <div>
          <label className="label-caps block mb-2">Email address</label>
          <input required type="email" data-testid="invite-email" autoFocus className="input-dark" value={form.email} onChange={(e)=>setForm({...form, email: e.target.value})}/>
        </div>
        <div>
          <label className="label-caps block mb-2">Role</label>
          <select data-testid="invite-role" className="input-dark" value={form.brand_role} onChange={(e)=>setForm({...form, brand_role: e.target.value})}>
            {BRAND_ROLES.map(r => <option key={r} value={r}>{roleLabel(r)}</option>)}
          </select>
        </div>
        <div>
          <label className="label-caps block mb-2">Optional message</label>
          <textarea rows={2} className="input-dark" value={form.message} onChange={(e)=>setForm({...form, message: e.target.value})}/>
        </div>
        {error && <div className="text-red-600 text-sm">{error}</div>}
        <button disabled={saving} className="btn-gold w-full inline-flex items-center justify-center gap-2 disabled:opacity-50" data-testid="invite-submit">
          <UsersIcon size={14}/> {saving ? "Sending…" : "Send invitation"}
        </button>
        <div className="text-[10px] text-[#737373] uppercase tracking-widest">If the invitee already has an account, they get instant access. Otherwise the invitation is auto-accepted when they register with this email.</div>
      </form>
    </Modal>
  );
}
